import axios from 'axios';
import {useLanguageData, queryClient} from "../App";
import {useDataContext} from "../views/SurveyContextProvider";
import {useIntl} from "react-intl";
import {getAuthConfig, getInstanceConfigValue} from "../utils";
import {getTopicDisplayName, getTopicDisplayNameFromTranslations} from "./topicUtils";
import {formatTranslationsToIntl} from "../Formats";
import _ from "lodash";
const auth = getAuthConfig()

/**
 * Merges Gemini-specific template keys from geminiConfig into processedConfig.
 * Call this before processSingleBotInstructions when building Gemini instructions.
 */
export function mergeGeminiTemplates(processedConfig, geminiConfig) {
    if (!geminiConfig) return processedConfig;
    if (geminiConfig.supportModuleBody) processedConfig.supportBotInstructionBody = geminiConfig.supportModuleBody;
    if (geminiConfig.supportTopicBody) processedConfig.supportTopicInstructionBody = geminiConfig.supportTopicBody;
    if (geminiConfig.multiModuleHeader) processedConfig.multiModuleHeader = geminiConfig.multiModuleHeader;
    if (geminiConfig.defaultFollowupType) processedConfig.defaultFollowupType = geminiConfig.defaultFollowupType;
    if (geminiConfig.supportRequestDefaultsWithTarget) processedConfig.supportRequestDefaultsWithTarget = geminiConfig.supportRequestDefaultsWithTarget;
    if (geminiConfig.supportRequestDefaultsNoTarget) processedConfig.supportRequestDefaultsNoTarget = geminiConfig.supportRequestDefaultsNoTarget;
    if (geminiConfig.supportneedNoRequest) processedConfig.supportneedNoRequest = geminiConfig.supportneedNoRequest;
    if (geminiConfig.supportneedCreateRequest) processedConfig.supportneedCreateRequest = geminiConfig.supportneedCreateRequest;
    if (geminiConfig.supportneedRequestWithConsent) processedConfig.supportneedRequestWithConsent = geminiConfig.supportneedRequestWithConsent;
    if (geminiConfig.supportTargetsNone) processedConfig.supportTargetsNone = geminiConfig.supportTargetsNone;
    return processedConfig;
}

/**
 * Resolves versioned topic fields (instructions, requesttype, followuptype)
 * with fallback to base values. Shared by both OpenAI and Gemini.
 */
function resolveTopicFields(topicCode, topic, bot) {
    const settings = bot?.config?.topicSettings?.[topicCode];

    const instructions = settings?.instructions ?? topic?.instructions ?? '';
    const requestType = settings?.requesttype ?? topic?.requesttype ?? '';
    const followupType = settings?.followuptype ?? topic?.followuptype ?? '';

    return { instructions, requestType, followupType };
}

/**
 * Builds topic instructions for a single bot module — OpenAI path.
 * Template variables: {supportTopicTitle}, {supportTopicCode}, {supportRequestTarget},
 * {instructions}, {requesttypeTranslation}, {followuptype}
 */
function buildOpenAITopicInstructions(bot, processedConfig, intl, topicDataSource) {
    if (!Array.isArray(bot.config?.supportTopics)) return '';

    return bot.config.supportTopics
        .map(topicCode => {
            const topic = topicDataSource[topicCode];
            if (!topic) return '';

            const { instructions, requestType, followupType } = resolveTopicFields(topicCode, topic, bot);

            return (processedConfig.supportTopicInstructionBody || '')
                .replace(/{supportTopicTitle}/g, topic.title || '')
                .replace(/{supportTopicCode}/g, topicCode || '')
                .replace(/{supportRequestTarget}/g, bot.config?.supportRequestTarget || '')
                .replace(/{instructions}/g, instructions)
                .replace(/{requesttypeTranslation}/g, requestType ? intl.formatMessage({
                    defaultMessage: requestType,
                    id: `bot.requestType.${requestType}`
                }) : '')
                .replace(/{followuptype}/g, followupType);
        })
        .filter(i => i)
        .join('\n\n');
}

/**
 * Builds topic instructions for a single bot module — Gemini path.
 * Template variables: {supportTopicTitle}, {supportTopicCode},
 * {instructions}, {supportneedLine}
 */
function buildGeminiTopicInstructions(bot, processedConfig, topicDataSource, translationMap) {
    if (!Array.isArray(bot.config?.supportTopics)) return '';

    const defaultFollowupType = processedConfig.defaultFollowupType || 'NOFOLLOWUP';
    const moduleSupporttype = bot.config?.supportRequestTarget || '';
    const forceNoRequest = !moduleSupporttype;

    return bot.config.supportTopics
        .map(topicCode => {
            const topic = topicDataSource[topicCode];
            if (!topic) return '';

            const { instructions, requestType, followupType } = resolveTopicFields(topicCode, topic, bot);

            // When no support request target, only include topics that have their own instructions
            if (forceNoRequest && !instructions) return '';

            let supportneedLine = '';
            if (forceNoRequest) {
                // Module-level "Do not create support requests" already covers this — no per-topic line needed
                supportneedLine = '';
            } else {
                const supporttype = topic.supporttype || moduleSupporttype;
                const resolvedFollowupType = followupType || defaultFollowupType;

                const formatSupportType = (code) => {
                    const name = translationMap?.['role.' + code];
                    return name && name !== code ? `${code} (${name})` : code;
                };

                const overrides = [];
                if (supporttype !== moduleSupporttype) overrides.push(`supporttype "${formatSupportType(supporttype)}"`);
                if (resolvedFollowupType !== defaultFollowupType) overrides.push(`followuptype "${resolvedFollowupType}"`);
                const overrideText = overrides.length > 0
                    ? ` Use ${overrides.join(' and ')} for this topic.`
                    : '';

                if (requestType === 'createRequest') {
                    supportneedLine = (processedConfig.supportneedCreateRequest || '')
                        .replace(/{overrideText}/g, overrideText);
                } else if (requestType === 'requestWithConsent') {
                    supportneedLine = (processedConfig.supportneedRequestWithConsent || '')
                        .replace(/{overrideText}/g, overrideText);
                } else {
                    supportneedLine = processedConfig.supportneedNoRequest || '';
                }
            }

            return (processedConfig.supportTopicInstructionBody || '')
                .replace(/{supportTopicTitle}/g, topic.title || '')
                .replace(/{supportTopicCode}/g, topicCode || '')
                .replace(/{instructions}/g, instructions)
                .replace(/{supportneedLine}/g, supportneedLine)
                .trim();
        })
        .filter(i => i)
        .join('\n\n');
}

/**
 * Builds the full instruction section for a single bot module.
 * Detects Gemini vs OpenAI by checking for Gemini-specific keys in processedConfig.
 */
export const processSingleBotInstructions = ({
                                                 bot,
                                                 processedConfig,
                                                 intl,
                                                 categoryData = null,
                                                 translationMap = null,
                                             }) => {
    const topicDataSource = categoryData || bot.config?.topicData || {};
    const isGeminiTemplate = !!processedConfig.supportneedNoRequest;

    // Build topic instructions
    const supportTopicInstructions = isGeminiTemplate
        ? buildGeminiTopicInstructions(bot, processedConfig, topicDataSource, translationMap)
        : buildOpenAITopicInstructions(bot, processedConfig, intl, topicDataSource);

    // Build bot instruction section from template
    let botInstructionSection = processedConfig.supportBotInstructionBody || '';

    if (isGeminiTemplate) {
        // Corpora sources for knowledge base reference
        let corporaSources = '';
        if (bot.config?.corpora && typeof bot.config.corpora === 'object') {
            const sources = Object.keys(bot.config.corpora);
            if (sources.length === 1) {
                corporaSources = `"${sources[0]}"`;
            } else if (sources.length > 1) {
                corporaSources = sources.map(s => `"${s}"`).join(', ');
            }
        }

        // Support request defaults line
        const defaultFollowupType = processedConfig.defaultFollowupType || 'NOFOLLOWUP';
        let supportRequestDefaults = '';
        if (bot.config?.supportRequestTarget) {
            const targetCode = bot.config.supportRequestTarget;
            const targetName = translationMap?.['role.' + targetCode];
            const targetDisplay = targetName && targetName !== targetCode
                ? `${targetCode} (${targetName})`
                : targetCode;
            supportRequestDefaults = (processedConfig.supportRequestDefaultsWithTarget || '')
                .replace(/{supportRequestTarget}/g, targetDisplay)
                .replace(/{defaultFollowupType}/g, defaultFollowupType);
        } else {
            supportRequestDefaults = processedConfig.supportRequestDefaultsNoTarget || '';
        }

        botInstructionSection = botInstructionSection
            .replace(/{moduleName}/g, bot.config?.name || '')
            .replace(/{moduleDescription}/g, bot.config?.description || '')
            .replace(/{generalInstructions}/g, bot.config?.generalInstructions || '')
            .replace(/{corporaSources}/g, corporaSources)
            .replace(/{supportRequestDefaults}/g, supportRequestDefaults)
            .replace(/{supportTopicInstructions}/g, supportTopicInstructions);
    } else {
        // OpenAI: formatted materials list
        let formattedMaterials = '';
        if (Array.isArray(bot.config?.materials) && bot.config.materials.length > 0) {
            formattedMaterials = bot.config.materials.map(material => {
                const materialName = typeof material === 'object' ? material.filename : material;
                return `- ${materialName}`;
            }).join('\n');
        }

        botInstructionSection = botInstructionSection
            .replace(/{supportRequestTargetTranslation}/g, bot.config?.supportRequestTarget ? intl.formatMessage({
                defaultMessage: bot.config?.supportRequestTarget,
                id: `role.${bot.config?.supportRequestTarget}`
            }) : '')
            .replace(/{generalInstructions}/g, bot.config?.generalInstructions || '')
            .replace(/{materials}/g, formattedMaterials)
            .replace(/{supportTopicInstructions}/g, supportTopicInstructions);
    }

    return botInstructionSection;
};

// Resolve a language code (e.g. "fi") to its English display name (e.g. "Finnish").
export const getLanguageDisplayName = (code) => {
    if (!code) return "";
    try {
        return new Intl.DisplayNames(["en"], { type: "language" }).of(code) || code;
    } catch (e) {
        return code;
    }
};

export const processAssistantInstructions = ({
                                                 assistantInstructionsConfig,
                                                 surveyData,
                                                 supportBots,
                                                 categoryData,
                                                 supportBotInstructions,
                                                 supportTopicInstructionBody,
                                                 supportBotInstructionBody,
                                                 moduleInstructionsLang,
                                                 intl,
                                                 surveyId,
                                                 isTest = false,
                                                 whatsAppInstance = false,
                                                 masterPromptOverride,
                                                 geminiConfig,
                                                 translationMap = null,
                                                 tone = null,
                                             }) => {
    const processedConfig = replaceInstructionVariables(
        assistantInstructionsConfig,
        supportBotInstructions,
        supportTopicInstructionBody,
        supportBotInstructionBody,
        moduleInstructionsLang
    );

    // For Gemini, override OpenAI templates with Gemini-specific ones
    if (geminiConfig) {
        mergeGeminiTemplates(processedConfig, geminiConfig);
    }

    if (processedConfig && typeof processedConfig === 'object' && processedConfig?.postBody) {
        // Set the assistant name with optional TEST prefix
        if (surveyData?.config?.title && surveyId) {
            const prefix = isTest ? '[TEST] ' : '';
            processedConfig.postBody.name = `${prefix}${surveyData.config.title} (${surveyId})`;
        }

        if (surveyData?.config?.useCustomInstructions && surveyData?.config?.customInstructions) {
            processedConfig.postBody.instructions = surveyData.config.customInstructions;
        } else {
            let allBotInstructions = '';
            let supportTargets = processedConfig.supportTargetsNone || '';

            if (Array.isArray(surveyData?.config?.supportBots) && supportBots) {
                const activeBots = surveyData.config.supportBots
                    .map(botId => supportBots.find(b => b.id === botId))
                    .filter(Boolean);

                // Top-level list of the only valid support request targets across all
                // active modules — keeps RAG-surfaced names from being offered as targets.
                const targetSet = new Set();
                activeBots.forEach(bot => {
                    if (bot.config?.supportRequestTarget) targetSet.add(bot.config.supportRequestTarget);
                });
                if (targetSet.size > 0) {
                    supportTargets = [...targetSet].map(code => {
                        const name = translationMap?.['role.' + code];
                        return name && name !== code ? `- ${code} (${name})` : `- ${code}`;
                    }).join('\n');
                }

                activeBots.forEach(bot => {
                    const botInstructionSection = processSingleBotInstructions({
                        bot,
                        processedConfig,
                        intl,
                        categoryData,
                        translationMap
                    });

                    allBotInstructions += botInstructionSection + '\n\n';
                });

                // Add multi-module routing header when multiple modules are active
                if (activeBots.length > 1 && processedConfig.multiModuleHeader) {
                    const moduleIndex = activeBots
                        .map(bot => `- ${bot.config?.name || 'Unknown'} — ${bot.config?.description || ''}`)
                        .join('\n');

                    const multiModuleHeader = processedConfig.multiModuleHeader
                        .replace(/{moduleIndex}/g, moduleIndex);

                    allBotInstructions = multiModuleHeader + '\n\n' + allBotInstructions;
                }
            }

            let initialMessage = surveyData?.config?.message || '';

            if (whatsAppInstance && surveyData?.config?.whatsappVariables && initialMessage) {
                // Replace variables in the message with whatsappVariables values
                Object.entries(surveyData.config.whatsappVariables).forEach(([key, value]) => {
                    const variablePattern = new RegExp(`{{${key}}}`, 'g');
                    initialMessage = initialMessage.replace(variablePattern, String(value));
                });
            }

            const masterPrompt = masterPromptOverride || processedConfig.postBody.instructions;
            if (masterPrompt && typeof masterPrompt === 'string') {
                const tones = geminiConfig?.tones;
                const communicationStyle = (tones && tone && tones[tone])
                    ? tones[tone]
                    : (tones?.professional ?? '');

                processedConfig.postBody.instructions = masterPrompt
                    .replace(/{botInstructions}/g, allBotInstructions.trim())
                    .replace(/{initialMessage}/g, initialMessage)
                    .replace(/{communicationStyle}/g, communicationStyle)
                    .replace(/{supportTargets}/g, supportTargets)
                    .replace(/{defaultLanguage}/g, getLanguageDisplayName(moduleInstructionsLang));
            }
        }
    }

    return processedConfig?.postBody;
};

export function replaceInstructionVariables(
    assistantInstructionsConfig,
    supportBotInstructions,
    supportTopicInstructionBody,
    supportBotInstructionBody,
    language = "en"
) {
    const getLanguageValue = (dataArray, lang = language) => {
        if (!Array.isArray(dataArray)) return dataArray || "";

        // Try to find exact language match
        const langEntry = dataArray.find(item => item.language === lang);
        if (langEntry) return langEntry.value || "";

        // Fallback to English if specified language not found
        if (lang !== "en") {
            const enEntry = dataArray.find(item => item.language === "en");
            if (enEntry) return enEntry.value || "";
        }

        // Fallback to first available non-"dc" entry if neither found
        if (dataArray.length > 0) {
            const nonDcEntry = dataArray.find(item => item.language !== "dc" && item.value);
            if (nonDcEntry) return nonDcEntry.value;
        }

        return "";
    };

    const config = _.cloneDeep(assistantInstructionsConfig);

    if (config.postBody?.instructions) {
        config.postBody.instructions = getLanguageValue(supportBotInstructions);
    }

    if (config.supportBotInstructionBody) {
        config.supportBotInstructionBody = getLanguageValue(supportBotInstructionBody);
    }

    if (config.supportTopicInstructionBody) {
        config.supportTopicInstructionBody = getLanguageValue(supportTopicInstructionBody);
    }

    return config;
}

export const fetchAssistantToolsAndResources = async (supportBotIds, supportBots, auth, provider = "openai") => {
    const combinedTools = [];
    const combinedToolResources = {};

    if (!Array.isArray(supportBotIds) || !supportBots) {
        return { tools: combinedTools, tool_resources: combinedToolResources };
    }

    // OpenAI: existing flow
    // Collect all assistant IDs from support bots (assistantid is at root level)
    const assistantIds = supportBotIds
        .map(botId => {
            const bot = supportBots.find(b => b.id === botId);
            return bot?.assistantid || null;
        })
        .filter(id => id !== null);

    try {
        const assistantPromises = assistantIds.map(async (asstId) => {
            try {
                const { data } = await axios.get(`/api/v2/openai.php/assistants/${asstId}`, auth);
                return data;
            } catch (error) {
                console.error(`Failed to fetch assistant ${asstId}:`, error);
                return null;
            }
        });

        const assistantDataArray = await Promise.all(assistantPromises);

        assistantDataArray.forEach(assistantData => {
            if (assistantData) {
                // Combine tools (avoiding duplicates based on type)
                if (Array.isArray(assistantData.tools)) {
                    assistantData.tools.forEach(tool => {
                        const existingTool = combinedTools.find(t => t.type === tool.type);
                        if (!existingTool) {
                            combinedTools.push(tool);
                        }
                    });
                }

                // Combine tool_resources
                if (assistantData.tool_resources) {
                    if (assistantData.tool_resources.file_search?.vector_store_ids) {
                        if (!combinedToolResources.file_search) {
                            combinedToolResources.file_search = {
                                vector_store_ids: [assistantData.tool_resources.file_search.vector_store_ids[0]]
                            };
                        }
                        // Skip adding more vector stores - OpenAI only supports one
                    }

                    // Handle code_interpreter resources
                    if (assistantData.tool_resources.code_interpreter?.file_ids) {
                        if (!combinedToolResources.code_interpreter) {
                            combinedToolResources.code_interpreter = { file_ids: [] };
                        }
                        assistantData.tool_resources.code_interpreter.file_ids.forEach(id => {
                            if (!combinedToolResources.code_interpreter.file_ids.includes(id)) {
                                combinedToolResources.code_interpreter.file_ids.push(id);
                            }
                        });
                    }
                }
            }
        });
    } catch (error) {
        console.error('Error fetching assistant tools and resources:', error);
    }

    return {
        tools: combinedTools.length > 0 ? combinedTools : undefined,
        tool_resources: Object.keys(combinedToolResources).length > 0 ? combinedToolResources : undefined
    };
};

/**
 * Gathers all file IDs from selected bot modules
 * @param {Array} botIds - Array of selected bot IDs
 * @param {Array} supportBots - All available support bots
 * @returns {Array} Array of unique file IDs
 */
export function gatherFilesFromBots(botIds, supportBots) {
    const fileIds = new Set();

    if (!botIds || !supportBots) {
        return [];
    }

    botIds.forEach(botId => {
        const bot = supportBots.find(b => Number(b.id) === Number(botId));
        if (bot?.config?.materials) {
            bot.config.materials.forEach(material => {
                // Only include valid files (not legacy)
                if (material.id && !material.isLegacy) {
                    fileIds.add(material.id);
                }
            });
        }
    });

    return Array.from(fileIds);
}

export const useAssistantOperations = () => {
    const { value: assistantInstructionsConfig } = useDataContext('assistantInstructionsConfig');
    const { value: supportBots } = useDataContext('supportBots');
    const { value: categoryData } = useDataContext('categoryData');
    const { value: supportBotInstructions } = useDataContext('supportBotInstructions');
    const { value: supportTopicInstructionBody } = useDataContext('supportTopicInstructionBody');
    const { value: supportBotInstructionBody } = useDataContext('supportBotInstructionBody');
    const { value: instanceConfig } = useDataContext('instanceConfig')
    const channelValue = getInstanceConfigValue("contact", "channel", instanceConfig);
    const whatsAppInstance = Array.isArray(channelValue)
        ? channelValue.some(channel => channel.toLowerCase().includes("whatsapp"))
        : channelValue?.toLowerCase()?.includes("whatsapp");
    const { instanceLang } = useLanguageData();
    const intl = useIntl();

    const buildAssistantInstructions = (surveyData, surveyId, isTest = false, masterPromptOverride, geminiConfigOverride, surveyLangTranslations, tone = null) => {
        // Always build from the freshest bots in the query cache (callers refetch "bots"
        // before save/test), falling back to the context snapshot if the cache is empty.
        const effectiveSupportBots = queryClient.getQueryData("bots") || supportBots;
        const moduleInstructionsLang = surveyData?.config?.language || instanceLang || "en";

        // Resolve taxonomy topic titles in the survey/bot language (not the admin UI language)
        const translationMap = surveyLangTranslations
            ? formatTranslationsToIntl(surveyLangTranslations)
            : null;
        const resolvedCategoryData = categoryData
            ? Object.fromEntries(
                Object.entries(categoryData).map(([code, data]) => [
                    code,
                    {
                        ...data,
                        title: translationMap
                            ? getTopicDisplayNameFromTranslations(code, data, translationMap)
                            : getTopicDisplayName(code, data, intl)
                    }
                ])
            )
            : {};

        return processAssistantInstructions({
            assistantInstructionsConfig,
            surveyData,
            supportBots: effectiveSupportBots,
            categoryData: resolvedCategoryData,
            supportBotInstructions,
            supportTopicInstructionBody,
            supportBotInstructionBody,
            moduleInstructionsLang,
            intl,
            surveyId,
            isTest,
            whatsAppInstance,
            masterPromptOverride,
            geminiConfig: geminiConfigOverride,
            translationMap,
            tone,
        });
    };

    const fetchCombinedToolsAndResources = async (supportBotIds, provider = "openai") => {
        const effectiveSupportBots = queryClient.getQueryData("bots") || supportBots;
        return fetchAssistantToolsAndResources(supportBotIds, effectiveSupportBots, auth, provider);
    };

    return {
        buildAssistantInstructions,
        fetchCombinedToolsAndResources
    };
};